import React, { useState, useEffect, useMemo } from 'react';
import { Calendar as BigCalendar, momentLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { adminService } from '../services/adminService';
import { availabilityService, TimeSlot } from '../services/availabilityService';
import { addDaysLA, nowInLA } from '../utils/timezone';

// Create localizer for date-fns
const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = momentLocalizer(require('moment'));

interface SlotEvent {
  title: string;
  start: Date;
  end: Date;
  available: boolean;
  time: string;
  reason?: 'booked' | 'admin' | string;
  appointmentId?: string;
  isBooked: boolean;
}

interface AdminCalendarViewRef {
  refresh: () => Promise<void>;
}

interface AdminCalendarViewProps {}

export const AdminCalendarView = React.forwardRef<AdminCalendarViewRef, AdminCalendarViewProps>((props, ref) => {
  const [view, setView] = useState<View>('week');
  const [date, setDate] = useState(addDaysLA(nowInLA(), 1));
  const [events, setEvents] = useState<SlotEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSlots();
  }, [date, view]);

  const loadSlots = async () => {
    try {
      setLoading(true);
      setError(null);

      let allEvents: SlotEvent[] = [];

      if (view === 'week') {
        // Load slots for entire week
        const startOfWeekDate = new Date(date);
        const dayOfWeek = startOfWeekDate.getDay();
        const diff = startOfWeekDate.getDate() - dayOfWeek;
        startOfWeekDate.setDate(diff);

        // Load 7 days
        for (let i = 0; i < 7; i++) {
          const currentDate = new Date(startOfWeekDate);
          currentDate.setDate(startOfWeekDate.getDate() + i);
          
          const dayEvents = await loadSlotsForDate(currentDate);
          allEvents = allEvents.concat(dayEvents);
        }
      } else if (view === 'month') {
        // For month view, load entire month (42 days to cover full calendar grid)
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const startDay = startOfMonth.getDay();
        
        // Go back to the Sunday of the week containing the 1st
        const calendarStart = new Date(startOfMonth);
        calendarStart.setDate(1 - startDay);

        // Load 42 days (6 weeks) to cover full month view
        for (let i = 0; i < 42; i++) {
          const currentDate = new Date(calendarStart);
          currentDate.setDate(calendarStart.getDate() + i);
          
          const dayEvents = await loadSlotsForDate(currentDate);
          allEvents = allEvents.concat(dayEvents);
        }
      } else {
        // Day view - load single day
        allEvents = await loadSlotsForDate(date);
      }

      setEvents(allEvents);
    } catch (err) {
      setError('Failed to load slots');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSlotsForDate = async (currentDate: Date): Promise<SlotEvent[]> => {
    // Load slots for the current date
    const timeSlots = await availabilityService.getAvailableTimeSlots(currentDate);
    
    // Convert time slots to calendar events
    const slotEvents: SlotEvent[] = timeSlots.map(slot => {
      const [time, period] = slot.time.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      
      if (period === 'PM' && hours !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hours === 12) {
        hour24 = 0;
      }

      const startDate = new Date(currentDate);
      startDate.setHours(hour24, minutes, 0, 0);
      
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + 30); // 30-minute slots

      const isBooked = slot.reason === 'booked';
      let title: string;
      
      if (slot.available) {
        title = '✓ Available';
      } else if (isBooked) {
        title = '📅 Booked';
      } else {
        title = '✗ Disabled';
      }

      return {
        title,
        start: startDate,
        end: endDate,
        available: slot.available,
        time: slot.time,
        reason: slot.reason,
        appointmentId: slot.appointmentId,
        isBooked
      };
    });

    return slotEvents;
  };

  const handleSelectEvent = async (event: SlotEvent) => {
    const dateStr = formatDateString(event.start);
    const actionKey = `${dateStr}_${event.time}`;
    
    // Don't allow toggling booked slots
    if (event.isBooked) {
      setError('Cannot toggle booked slots. Cancel the appointment first.');
      return;
    }
    
    try {
      setActionLoading(actionKey);
      setError(null);

      if (event.available) {
        await adminService.disableSlot(dateStr, event.time);
      } else {
        await adminService.enableSlot(dateStr, event.time);
      }

      // Reload slots
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to update slot');
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const eventStyleGetter = (event: SlotEvent) => {
    let backgroundColor: string;
    let cursor: string;
    
    if (event.available) {
      backgroundColor = '#22c55e'; // Green for available
      cursor = 'pointer';
    } else if (event.isBooked) {
      backgroundColor = '#f59e0b'; // Orange for booked
      cursor = 'not-allowed';
    } else {
      backgroundColor = '#ef4444'; // Red for disabled
      cursor = 'pointer';
    }
    
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block',
      cursor
    };
    return { style };
  };

  // Expose the loadSlots function to parent component
  React.useImperativeHandle(ref, () => ({
    refresh: loadSlots
  }));

  // Configure calendar to show business hours only
  const minTime = new Date();
  minTime.setHours(9, 0, 0);
  
  const maxTime = new Date();
  maxTime.setHours(16, 0, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Manage Available Slots - Calendar View</CardTitle>
          <CardDescription>
            Click on any slot to toggle its availability (Los Angeles timezone: 9:00 AM - 3:30 PM)
          </CardDescription>
        </div>
        <Button onClick={loadSlots} variant="outline" size="sm" disabled={loading}>
          Refresh Calendar
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="bg-white p-4 rounded-lg border" style={{ height: '800px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Loading calendar...</p>
            </div>
          ) : (
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              min={minTime}
              max={maxTime}
              step={30}
              timeslots={1}
              defaultView="week"
              views={['month', 'week', 'day']}
            />
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span className="text-sm">Booked</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span className="text-sm">Disabled</span>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Click available or disabled slots to toggle. Booked slots require appointment cancellation.
          </p>
        </div>
      </CardContent>
    </Card>
  );
});

AdminCalendarView.displayName = 'AdminCalendarView';
